/**
 * @typedef {[number, number]} Point
 */

/**
 * @typedef {{ left: number, top: number, width: number, height: number }} OffsetRect
 */

/**
 * @typedef {{ startDirection?: ("vertical" | "horizontal") }} PathOptions
 */

/**
 * @typedef {{ idFrom: string, idTo: string, options?: Record<string, PathOptions> }} Connection
 */

class GridHelper {
	static gridSize = 1;

	/**
	 * @param {HTMLElement} el
	 * @returns {OffsetRect}
	 */
	static getOffsetRect(el) {
		return {
			left: el.offsetLeft,
			top: el.offsetTop,
			width: el.clientWidth,
			height: el.clientHeight,
		};
	}

	/**
	 * @param {number} x
	 * @param {number} y
	 * @returns {Point}
	 */
	static toGridCoords(x, y) {
		return [
			Math.round(x / this.gridSize) * this.gridSize,
			Math.round(y / this.gridSize) * this.gridSize,
		];
	}

	/**
	 * @param {OffsetRect} offsetRect
	 * @returns {Point}
	 */
	static getBoxMid(offsetRect) {
		return GridHelper.toGridCoords(
			offsetRect.left + offsetRect.width / 2,
			offsetRect.top + offsetRect.height / 2,
		);
	}

	/**
	 * @param {Point} point1
	 * @param {Point} point2
	 * @returns {boolean}
	 */
	static isSamePoint(point1, point2) {
		if (point1[0] == point2[0] && point1[1] == point2[1]) {
			return true;
		}
		return false;
	}

	/**
	 * @param {Point} start
	 * @param {Point} to
	 * @param {HTMLElement[]} squares
	 * @param {boolean} isVertical
	 * @param {Point} end
	 * @returns {boolean}
	 */
	static isPathClean(start, to, squares, isVertical, end) {
		let tolerance = 2;

		for (let square of squares) {
			let sq = GridHelper.getOffsetRect(square);
			if (!this.isSamePoint(start, this.getBoxMid(sq))) {
				if (isVertical) {
					if (this.isSamePoint(end, this.getBoxMid(sq))) {
						// entry point on end
						if (end[0] == start[0]) {
							return true;
						}
					}

					if (
						sq.left < start[0] + tolerance &&
						sq.left + sq.width > start[0] - tolerance &&
						sq.top > Math.min(start[1], to[1]) &&
						sq.top < Math.max(start[1], to[1])
					) {
						return false;
					}
				} else {
					if (this.isSamePoint(end, this.getBoxMid(sq))) {
						// entry point on end
						if (end[1] == start[1]) {
							return true;
						}
					}

					if (
						sq.top < start[1] + tolerance &&
						sq.top + sq.height > start[1] - tolerance &&
						sq.left > Math.min(start[0], to[0]) &&
						sq.left < Math.max(start[0], to[0])
					) {
						return false;
					}
				}
			}
		}
		return true;
	}

	/**
	 * @param {Point} start
	 * @param {Point} end
	 * @param {HTMLElement[]} squares
	 * @param {boolean} isVertical
	 * @returns {OffsetRect | null}
	 */
	static findClosestObstacle(start, end, squares, isVertical) {
		let closestObstacle = null;
		let minDistance = Infinity;
		let clSquare = null;
		for (let square of squares) {
			let sq = GridHelper.getOffsetRect(square);

			if (!this.isSamePoint(start, this.getBoxMid(sq))) {
				if (isVertical) {
					if (
						sq.left < start[0] + 10 &&
						sq.left + sq.width > start[0] - 10 &&
						sq.top > Math.min(start[1], end[1]) &&
						sq.top < Math.max(start[1], end[1])
					) {
						let distance = Math.abs(start[1] - sq.top);
						if (distance < minDistance) {
							minDistance = distance;
							closestObstacle = sq;
							clSquare = square;
						}
					}
				} else {
					if (
						sq.top < start[1] + 10 &&
						sq.top + sq.height > start[1] - 10 &&
						sq.left > Math.min(start[0], end[0]) &&
						sq.left < Math.max(start[0], end[0])
					) {
						let distance = Math.abs(start[0] - sq.left);
						if (distance < minDistance) {
							minDistance = distance;
							closestObstacle = sq;
							clSquare = square;
						}
					}
				}
			}
		}
		return closestObstacle;
	}
}

class PathCalculator {
	/**
	 * Computes an orthogonal path from `fromBox` to `toBox` while avoiding obstacles.
	 *
	 * @param {OffsetRect} fromBox
	 * @param {OffsetRect} toBox
	 * @param {HTMLElement[]} squares
	 * @param {PathOptions} options
	 * @returns {Point[]}
	 */
	static calculateOptimizedPath(fromBox, toBox, squares, options) {
		let start = GridHelper.getBoxMid(fromBox);
		let end = GridHelper.getBoxMid(toBox);
		let path = [start];
		let current = start;

		function getCollision(direction) {
			if (direction == "vertical") {
				return GridHelper.findClosestObstacle(
					current,
					[current[0], end[1]],
					squares,
					true,
				);
			}
			return GridHelper.findClosestObstacle(
				current,
				[end[0], current[1]],
				squares,
				false,
			);
		}

		function isPathClean(direction, end) {
			if (direction == "vertical") {
				return GridHelper.isPathClean(
					current,
					[current[0], end[1]],
					squares,
					true,
					end,
				);
			}
			return GridHelper.isPathClean(
				current,
				[end[0], current[1]],
				squares,
				false,
				end,
			);
		}

		function yStep(collisionDistance) {
			if (!collisionDistance) {
				current = [current[0], end[1]];
				path.push(current);
			} else {
				let midY = current[1] + collisionDistance / 2;
				current = [current[0], midY];
				path.push(current);
			}
		}

		function xStep(collisionDistance) {
			if (!collisionDistance) {
				current = [end[0], current[1]];
				path.push(current);
			} else {
				let midX = current[0] + collisionDistance / 2;
				current = [midX, current[1]];
				path.push(current);
			}
		}

		function getMid(closest) {
			return closest ? GridHelper.getBoxMid(closest) : null;
		}

		function getDistance(point, direction) {
			if (direction == "vertical") {
				return point ? Math.abs(current[1] - point[1]) : null;
			}
			return point ? Math.abs(current[0] - point[0]) : null;
		}

		let counter = 0;
		while (!GridHelper.isSamePoint(current, end)) {
			if (counter == 0 && options.startDirection === "vertical") {
				yStep(getDistance(getMid(getCollision("vertical")), "vertical"));
			} else if (counter == 0 && options.startDirection === "horizontal") {
				xStep(getDistance(getMid(getCollision("horizontal")), "horizontal"));
			} else {
				if (isPathClean("vertical", end) && current[1] != end[1]) {
					yStep(null);
				} else if (isPathClean("horizontal", end) && current[0] != end[0]) {
					xStep(null);
				} else {
					const vMid = getMid(getCollision("vertical"));
					const hMid = getMid(getCollision("horizontal"));
					let verticalDistanceToEnd = vMid
						? Math.abs(end[1] - vMid[1])
						: Infinity;
					let horizontalDistanceToEnd = hMid
						? Math.abs(end[0] - hMid[0])
						: Infinity;

					if (
						verticalDistanceToEnd &&
						horizontalDistanceToEnd &&
						verticalDistanceToEnd < horizontalDistanceToEnd
					) {
						yStep(getDistance(vMid), "vertical");
					} else {
						xStep(getDistance(hMid), "horizontal");
					}
				}
			}
			counter++;
		}
		return path;
	}

	/**
	 * @param {Point[]} points
	 * @returns {string}
	 */
	static pointsToPathD(points) {
		return (
			`M ${points[0][0]},${points[0][1]} ` +
			points
				.slice(1)
				.map((p) => `L ${p[0]},${p[1]}`)
				.join(" ")
		);
	}
}

/**
 * Draws SVG paths between DOM elements (by id) within a container.
 */
export default class PathConnector {
	/**
	 * @param {string} containerId
	 * @param {Connection[]} connections
	 * @param {{ value: string }} mediaSizeRef
	 */
	constructor(containerId, connections, mediaSizeRef) {
		this.container = document.getElementById(containerId);
		this.squares = Array.from(
			this.container.querySelectorAll(".iam-findercard"),
		);
		this.svgLayer = this.createSvgLayer();
		this.connections = connections;
		this.mediaSizeRef = mediaSizeRef;

		this.drawConnections();
	}

	/** @returns {{ width: number, height: number }} */
	calculateContainerSize() {
		let minX = Infinity,
			minY = Infinity,
			maxX = 0,
			maxY = 0;

		this.squares.forEach((el) => {
			const rect = el.getBoundingClientRect();
			minX = Math.min(minX, rect.left);
			minY = Math.min(minY, rect.top);
			maxX = Math.max(maxX, rect.right);
			maxY = Math.max(maxY, rect.bottom);
		});

		return {
			width: maxX - minX,
			height: maxY - minY,
		};
	}

	/** @returns {SVGSVGElement} */
	createSvgLayer() {
		const boundingBox = this.calculateContainerSize();
		let svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

		svg.setAttribute("width", boundingBox.width);
		svg.setAttribute("height", boundingBox.height);
		svg.style.position = "absolute";
		svg.style.left = "0";
		svg.style.top = "0";

		this.container.appendChild(svg);
		return svg;
	}

	/** @returns {void} */
	drawConnections() {
		this.connections.forEach((conn) => {
			const fromEl = document.getElementById(conn.idFrom);
			const toEl = document.getElementById(conn.idTo);
			if (!fromEl || !toEl) return;

			const fromBox = GridHelper.getOffsetRect(fromEl);
			const toBox = GridHelper.getOffsetRect(toEl);

			const options = conn.options?.[this.mediaSizeRef.value] ?? {};

			const pathXY = PathCalculator.calculateOptimizedPath(
				fromBox,
				toBox,
				this.squares,
				options,
			);
			const dString = PathCalculator.pointsToPathD(pathXY);

			this.drawPath(dString);
		});
	}

	/**
	 * @param {string} dString
	 * @returns {void}
	 */
	drawPath(dString) {
		const pathEl = document.createElementNS(
			"http://www.w3.org/2000/svg",
			"path",
		);
		pathEl.setAttribute("d", dString);
		pathEl.setAttribute("stroke", "#157bc0");
		pathEl.setAttribute("stroke-width", "2");
		pathEl.setAttribute("fill", "none");
		this.svgLayer.appendChild(pathEl);
	}

	/** @returns {void} */
	update() {
		this.svgLayer.remove();

		this.squares = Array.from(
			this.container.querySelectorAll(".iam-findercard"),
		);
		this.svgLayer = this.createSvgLayer();

		this.drawConnections();
	}
}
